// ============================================================
// TransfersTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
// Finished tape moving from the factory to the shop, and the
// record of what has already moved.
//
// WHY STOCK HAS TO MOVE AT ALL
// Cutting delivers into the factory. Invoices draw from the
// shop. Nothing is sold straight off the production floor, so
// a spec has to be sent over before it can appear on a sales
// screen — which is also the moment the shopkeeper learns what
// it cost him.
//
// COST TRAVELS WITH THE GOODS
// Each line carries the cost of the exact pieces that moved,
// not the spec's current average. The shop then blends what it
// holds into one figure per spec, and that blend is the number
// the shopkeeper prices against.
//
// ONE WAY ONLY
// Stock does not come back. A transfer can be reversed while
// none of its pieces have been sold — that is a correction,
// not a return — and refuses once selling has started, because
// by then no arithmetic says which of those pieces are still
// standing.
//
// OPENING STOCK
// What was on the shop's shelves before this system existed.
// It never passed through the factory, so it is entered
// directly: the spec, the quantity, and what it cost. Behind
// the scenes it gets a production record of its own so sales
// can cost it the same way as everything else.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. TransferForm       factory → shop
//   C. OpeningStockForm   one-time entry
//   D. TransfersTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, X, Check, ChevronDown as Chevron,
  ArrowRight, PackagePlus,
} from 'lucide-react';
import WindowModal, { todayISO } from '../components/WindowModal';
import { hasFullAccess } from '../../../utils/access';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  warningBg:  '#fef3c7',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const num    = (n) => Number(n || 0);
const money  = (n) => `Rs ${num(n).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const money2 = (n) => `Rs ${num(n).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');

// Postgres numerics arrive as strings with trailing zeros
const mm = (n) => `${num(n).toLocaleString('en-PK', { maximumFractionDigits: 2 })} mm`;
const yd = (n) => `${num(n).toLocaleString('en-PK', { maximumFractionDigits: 2 })} yd`;

const specLabel = (r) =>
  `${r.tape_type_name} ${num(r.micron_value)}µ · ${mm(r.width_mm)} × ${yd(r.length_yards)}`;


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, required, hint, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
      {required && <span style={{ color: C.danger, marginLeft: '3px' }}>*</span>}
    </label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{hint}</div>}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, disabled, title, children, ...props }) => (
  <button
    title={title}
    disabled={disabled}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: disabled ? C.textMuted : (danger ? C.danger : C.textSoft),
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.45 : 1,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

const StatCard = ({ label, value, colour = C.text, sub }) => (
  <div style={{
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '10px', padding: '16px 18px',
  }}>
    <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
      {label}
    </div>
    <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>{sub}</div>}
  </div>
);


// ============================================================
// B. TRANSFER FORM  (factory → shop)
//
// Multi-line, because a load going over is rarely one spec.
// Each line picks from what the factory actually holds, and a
// spec already on the form drops out of the remaining pickers
// — the backend rejects a duplicate spec anyway, and finding
// that out after typing three lines is a poor way to learn it.
// ============================================================

const TransferForm = ({ stock, form, setForm, onSave, onCancel, saving, canSeeCost }) => {
  const chosen = new Set(form.items.map(i => String(i.stock_id)).filter(Boolean));

  const rowFor = (stockId) => stock.find(s => String(s.stock_id) === String(stockId));

  const setLine = (idx, patch) => {
    const items = form.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setForm({ ...form, items });
  };

  const addLine = () =>
    setForm({ ...form, items: [...form.items, { stock_id: '', quantity: '' }] });

  const removeLine = (idx) =>
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  // What the whole transfer is worth, at the cost of the pieces
  // moving. Only an estimate on this screen: the exact figure
  // comes from the cost layers the server draws.
  const estimate = form.items.reduce((sum, it) => {
    const row = rowFor(it.stock_id);
    return row ? sum + (Number(it.quantity) || 0) * num(row.cost_per_piece) : sum;
  }, 0);

  const anyOver = form.items.some(it => {
    const row = rowFor(it.stock_id);
    return row && Number(it.quantity) > Number(row.quantity);
  });

  const canSave =
    !saving &&
    !!form.transfer_date &&
    form.items.length > 0 &&
    form.items.every(it => it.stock_id && Number(it.quantity) > 0) &&
    !anyOver;

  return (
    <WindowModal
      id="stock-transfer-form"
      title="Send stock to shop"
      subtitle="Factory → shop. Cost travels with the pieces."
      onClose={onCancel}
      width="880px"
    >
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        gap: '16px', marginBottom: '20px',
      }}>
        <Field label="Transfer date" required>
          <TextInput
            type="date"
            max={todayISO()}
            value={form.transfer_date}
            onChange={e => setForm({ ...form, transfer_date: e.target.value })}
          />
        </Field>

        <Field label="Notes">
          <TextInput
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

      <div style={{
        fontSize: '12px', fontWeight: '600', color: C.textSoft,
        textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '10px',
      }}>
        What is going
      </div>

      {form.items.length === 0 && (
        <div style={{
          background: C.infoBg, color: C.info, borderRadius: '8px',
          padding: '13px 16px', fontSize: '13px', marginBottom: '14px',
        }}>
          Nothing added yet. Add a line to start.
        </div>
      )}

      {form.items.map((it, idx) => {
        const row   = rowFor(it.stock_id);
        const avail = row ? Number(row.quantity) : 0;
        const qty   = Number(it.quantity) || 0;
        const over  = row && qty > avail;

        return (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 150px 130px 40px',
              gap: '12px', alignItems: 'flex-start', marginBottom: '12px',
            }}
          >
            <div>
              <select
                style={inputStyle}
                value={it.stock_id}
                onChange={e => setLine(idx, { stock_id: e.target.value, quantity: '' })}
              >
                <option value="">Select stock…</option>
                {stock
                  .filter(s => String(s.stock_id) === String(it.stock_id)
                            || !chosen.has(String(s.stock_id)))
                  .map(s => (
                    <option key={s.stock_id} value={s.stock_id}>
                      {specLabel(s)} — {s.quantity} pc
                    </option>
                  ))}
              </select>
              {row && canSeeCost && (
                <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>
                  {money2(row.cost_per_piece)} each at the factory
                </div>
              )}
            </div>

            <div>
              <TextInput
                type="number" min="1" step="1"
                value={it.quantity}
                disabled={!row}
                onChange={e => setLine(idx, { quantity: e.target.value })}
                placeholder="Pieces"
              />
              {row && (
                <div style={{ fontSize: '11px', marginTop: '5px' }}>
                  <span style={{ color: over ? C.danger : C.textMuted }}>
                    {avail} available
                  </span>
                  {!over && qty !== avail && (
                    <button
                      type="button"
                      onClick={() => setLine(idx, { quantity: String(avail) })}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: C.brand, fontWeight: '600', fontSize: '11px',
                        fontFamily: 'inherit', padding: '0 0 0 7px',
                      }}
                    >
                      send all
                    </button>
                  )}
                </div>
              )}
            </div>

            <div style={{
              paddingTop: '11px', fontSize: '13px', textAlign: 'right',
              color: C.textSoft, fontWeight: '600',
            }}>
              {row && qty > 0 && canSeeCost
                ? money(qty * num(row.cost_per_piece))
                : ''}
            </div>

            <div style={{ paddingTop: '4px' }}>
              <IconBtn danger title="Remove line" onClick={() => removeLine(idx)}>
                <Trash2 size={15} />
              </IconBtn>
            </div>
          </div>
        );
      })}

      <div style={{ marginTop: '4px', marginBottom: '18px' }}>
        <Btn
          variant="ghost"
          onClick={addLine}
          disabled={stock.length > 0 && chosen.size >= stock.length}
        >
          <Plus size={16} /> Add line
        </Btn>
      </div>

      {anyOver && (
        <div style={{
          background: C.dangerBg, color: C.danger, borderRadius: '8px',
          padding: '13px 16px', marginBottom: '18px', fontSize: '13px',
        }}>
          One of the lines asks for more pieces than the factory holds.
        </div>
      )}

      {canSeeCost && estimate > 0 && (
        <div style={{
          background: C.rowAlt, border: `1px solid ${C.border}`,
          borderRadius: '8px', padding: '13px 18px', marginBottom: '18px',
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
          fontSize: '13px', color: C.textSoft,
        }}>
          <span>Moving to the shop at cost</span>
          <span style={{ marginLeft: 'auto', fontWeight: '700', fontSize: '15px', color: C.text }}>
            {money(estimate)}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Btn onClick={onSave} disabled={!canSave}>
          <Check size={16} /> {saving ? 'Sending…' : 'Send to shop'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </WindowModal>
  );
};


// ============================================================
// C. OPENING STOCK FORM
//
// Nothing to pick from — these specs predate the system, so
// every field is typed. The cost is per piece because that is
// the number the shopkeeper knows; the total is shown back so
// a slipped decimal is visible before saving.
// ============================================================

const OpeningStockForm = ({ types, microns, form, setForm, onSave, onCancel, saving }) => {
  const setLine = (idx, patch) => {
    const items = form.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setForm({ ...form, items });
  };

  const addLine = () => setForm({
    ...form,
    items: [...form.items, {
      tape_type_id: '', micron_id: '', width_mm: '',
      length_yards: '', quantity: '', cost_per_piece: '',
    }],
  });

  const removeLine = (idx) =>
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const lineTotal = (it) => (Number(it.quantity) || 0) * (Number(it.cost_per_piece) || 0);
  const total = form.items.reduce((sum, it) => sum + lineTotal(it), 0);

  // The same spec twice would create two production records
  // for one shelf. The totals would come out right, which is
  // exactly why it is worth catching — nothing downstream
  // would ever reveal the duplicate.
  const keys = form.items
    .filter(it => it.tape_type_id && it.micron_id && it.width_mm && it.length_yards)
    .map(it => [it.tape_type_id, it.micron_id, it.width_mm, it.length_yards].join('|'));
  const hasDuplicate = new Set(keys).size !== keys.length;

  const lineComplete = (it) =>
    it.tape_type_id && it.micron_id &&
    Number(it.width_mm) > 0 && Number(it.length_yards) > 0 &&
    Number.isInteger(Number(it.quantity)) && Number(it.quantity) > 0 &&
    Number(it.cost_per_piece) >= 0 && it.cost_per_piece !== '';

  const canSave =
    !saving &&
    !!form.transfer_date &&
    form.items.length > 0 &&
    form.items.every(lineComplete) &&
    !hasDuplicate;

  return (
    <WindowModal
      id="opening-stock-form"
      title="Shop opening stock"
      subtitle="What was already on the shop's shelves before this system."
      onClose={onCancel}
      width="980px"
    >
      <div style={{
        background: C.warningBg, color: C.warning, borderRadius: '8px',
        padding: '13px 16px', marginBottom: '18px', fontSize: '13px', lineHeight: 1.5,
      }}>
        This goes straight to the shop and cannot be deleted afterwards — invoices
        may be costed against it within minutes. Check the figures before saving.
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        gap: '16px', marginBottom: '20px',
      }}>
        <Field label="As at date" required>
          <TextInput
            type="date"
            max={todayISO()}
            value={form.transfer_date}
            onChange={e => setForm({ ...form, transfer_date: e.target.value })}
          />
        </Field>

        <Field label="Notes">
          <TextInput
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

      <div style={{
        fontSize: '12px', fontWeight: '600', color: C.textSoft,
        textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '10px',
      }}>
        What is on the shelf
      </div>

      {form.items.length === 0 && (
        <div style={{
          background: C.infoBg, color: C.info, borderRadius: '8px',
          padding: '13px 16px', fontSize: '13px', marginBottom: '14px',
        }}>
          Nothing added yet. Add a line for each size on the shelf.
        </div>
      )}

      {form.items.map((it, idx) => (
        <div
          key={idx}
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr 0.9fr 0.9fr 0.9fr 1fr 100px 40px',
            gap: '10px', alignItems: 'flex-start', marginBottom: '12px',
          }}
        >
          <select
            style={inputStyle}
            value={it.tape_type_id}
            onChange={e => setLine(idx, { tape_type_id: e.target.value })}
          >
            <option value="">Tape type…</option>
            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>

          <select
            style={inputStyle}
            value={it.micron_id}
            onChange={e => setLine(idx, { micron_id: e.target.value })}
          >
            <option value="">Micron…</option>
            {microns.map(m => (
              <option key={m.id} value={m.id}>{num(m.micron)}µ</option>
            ))}
          </select>

          <TextInput
            type="number" step="0.01" min="0"
            value={it.width_mm}
            onChange={e => setLine(idx, { width_mm: e.target.value })}
            placeholder="Width mm"
          />

          <TextInput
            type="number" step="0.01" min="0"
            value={it.length_yards}
            onChange={e => setLine(idx, { length_yards: e.target.value })}
            placeholder="Length yd"
          />

          <TextInput
            type="number" min="1" step="1"
            value={it.quantity}
            onChange={e => setLine(idx, { quantity: e.target.value })}
            placeholder="Pieces"
          />

          <TextInput
            type="number" step="0.01" min="0"
            value={it.cost_per_piece}
            onChange={e => setLine(idx, { cost_per_piece: e.target.value })}
            placeholder="Cost each"
          />

          <div style={{
            paddingTop: '11px', fontSize: '13px', textAlign: 'right',
            color: C.textSoft, fontWeight: '600',
          }}>
            {lineTotal(it) > 0 ? money(lineTotal(it)) : ''}
          </div>

          <div style={{ paddingTop: '4px' }}>
            <IconBtn danger title="Remove line" onClick={() => removeLine(idx)}>
              <Trash2 size={15} />
            </IconBtn>
          </div>
        </div>
      ))}

      <div style={{ marginTop: '4px', marginBottom: '18px' }}>
        <Btn variant="ghost" onClick={addLine}>
          <Plus size={16} /> Add line
        </Btn>
      </div>

      {hasDuplicate && (
        <div style={{
          background: C.dangerBg, color: C.danger, borderRadius: '8px',
          padding: '13px 16px', marginBottom: '18px', fontSize: '13px',
        }}>
          The same size appears on more than one line. Combine them into one.
        </div>
      )}

      {total > 0 && (
        <div style={{
          background: C.rowAlt, border: `1px solid ${C.border}`,
          borderRadius: '8px', padding: '13px 18px', marginBottom: '18px',
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
          fontSize: '13px', color: C.textSoft,
        }}>
          <span>Opening stock value</span>
          <span style={{ marginLeft: 'auto', fontWeight: '700', fontSize: '15px', color: C.text }}>
            {money(total)}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Btn onClick={onSave} disabled={!canSave}>
          <Check size={16} /> {saving ? 'Saving…' : 'Record opening stock'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </WindowModal>
  );
};


// ============================================================
// D. MAIN COMPONENT
// ============================================================

const emptyTransfer = () => ({
  transfer_date: todayISO(),
  notes: '',
  items: [{ stock_id: '', quantity: '' }],
});

const emptyOpening = () => ({
  transfer_date: todayISO(),
  notes: '',
  items: [{
    tape_type_id: '', micron_id: '', width_mm: '',
    length_yards: '', quantity: '', cost_per_piece: '',
  }],
});

const TransfersTab = () => {
  const [transfers, setTransfers] = useState([]);
  const [stock, setStock]         = useState([]);
  const [summary, setSummary]     = useState(null);
  const [types, setTypes]         = useState([]);
  const [microns, setMicrons]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [banner, setBanner]       = useState({ type: '', message: '' });

  const [transferForm, setTransferForm] = useState(null);
  const [openingForm, setOpeningForm]   = useState(null);
  const [expandedId, setExpandedId]     = useState(null);
  const [detail, setDetail]             = useState({});

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // The server strips cost for non-owners regardless. This only
  // decides whether to leave a gap where the columns would be.
  const role = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}').role;
    } catch {
      return null;
    }
  })();
  const canSeeCost = hasFullAccess('adhesive');

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [t, s, sum, st] = await Promise.all([
        axios.get(`${API}/tape/transfers`, { headers }),
        axios.get(`${API}/tape/transfers/stock`, { headers }),
        axios.get(`${API}/tape/transfers/summary`, { headers }),
        axios.get(`${API}/tape/settings/tape-types`, { headers }),
      ]);
      setTransfers(t.data.data || []);
      setStock(s.data.data || []);
      setSummary(sum.data.data || null);
      setTypes(st.data.data || []);
    } catch {
      notify('error', 'Could not load transfers. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Microns are only needed by the opening stock form, so they
  // are fetched when it opens rather than on every page load.
  const openOpening = async () => {
    try {
      if (microns.length === 0) {
        const res = await axios.get(`${API}/tape/settings/microns`, { headers });
        setMicrons((res.data.data || []).filter(m => m.is_active !== false));
      }
      setOpeningForm(emptyOpening());
    } catch {
      notify('error', 'Could not load microns');
    }
  };

  const toggleRow = async (row) => {
    if (expandedId === row.id) { setExpandedId(null); return; }
    setExpandedId(row.id);

    if (!detail[row.id]) {
      try {
        const res = await axios.get(`${API}/tape/transfers/${row.id}`, { headers });
        setDetail(d => ({ ...d, [row.id]: res.data.data }));
      } catch {
        notify('error', 'Could not load that transfer');
      }
    }
  };

  const saveTransfer = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/tape/transfers`, {
        transfer_date: transferForm.transfer_date,
        notes:         transferForm.notes,
        items: transferForm.items.map(it => ({
          stock_id: it.stock_id,
          quantity: Number(it.quantity),
        })),
      }, { headers });

      notify('success', 'Stock sent to the shop');
      setTransferForm(null);
      setDetail({});
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not send');
    } finally {
      setSaving(false);
    }
  };

  const saveOpening = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/tape/transfers/opening`, {
        transfer_date: openingForm.transfer_date,
        notes:         openingForm.notes,
        items: openingForm.items.map(it => ({
          tape_type_id:   it.tape_type_id,
          micron_id:      it.micron_id,
          width_mm:       Number(it.width_mm),
          length_yards:   Number(it.length_yards),
          quantity:       Number(it.quantity),
          cost_per_piece: Number(it.cost_per_piece),
        })),
      }, { headers });

      notify('success', 'Opening stock recorded');
      setOpeningForm(null);
      setDetail({});
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Reverse ${row.transfer_number}? The pieces go back to the factory.`
    )) return;

    try {
      await axios.delete(`${API}/tape/transfers/${row.id}`, { headers });
      notify('success', 'Transfer reversed');
      if (expandedId === row.id) setExpandedId(null);
      setDetail({});
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not reverse');
    }
  };

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        <StatCard
          label="At the factory"
          value={summary?.factory_pieces ?? '—'}
          sub={canSeeCost && summary ? `${money(summary.factory_value)} at cost` : 'pieces'}
        />
        <StatCard
          label="At the shop"
          value={summary?.shop_pieces ?? '—'}
          colour={C.brand}
          sub={canSeeCost && summary ? `${money(summary.shop_value)} at cost` : 'pieces'}
        />
        <StatCard
          label="Transfers"
          value={summary?.transfer_count ?? '—'}
          sub={summary ? `${summary.transfers_this_month} this month` : undefined}
        />
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Stock transfers
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Factory to shop. Click a transfer to see what moved.
            </p>
          </div>

          <Btn variant="ghost" onClick={openOpening}>
            <PackagePlus size={16} /> Opening stock
          </Btn>

          <Btn
            onClick={() => setTransferForm(emptyTransfer())}
            disabled={loading || stock.length === 0}
            title={stock.length === 0 ? 'Nothing at the factory to send' : undefined}
          >
            <ArrowRight size={16} /> Send to shop
          </Btn>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
            <thead>
              <tr>
                <th style={thStyle}>Transfer</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Notes</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Sizes</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Pieces</th>
                {canSeeCost && <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>}
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={canSeeCost ? 7 : 6} style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && transfers.map(row => {
                const isOpen  = expandedId === row.id;
                const opening = row.is_opening;
                const d       = detail[row.id];

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => toggleRow(row)}
                      style={{ cursor: 'pointer', background: isOpen ? C.rowAlt : 'transparent' }}
                    >
                      <td style={{ ...tdStyle, fontWeight: '700' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                          <Chevron
                            size={15}
                            style={{
                              color: C.textMuted,
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                          {row.transfer_number}
                          {opening && (
                            <span
                              title="Stock that was already at the shop before this system"
                              style={{
                                fontSize: '11px', fontWeight: '600',
                                padding: '2px 8px', borderRadius: '10px',
                                background: C.infoBg, color: C.info,
                              }}
                            >
                              Opening
                            </span>
                          )}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, color: C.textSoft, whiteSpace: 'nowrap' }}>
                        {fmtDate(row.transfer_date)}
                      </td>
                      <td style={{
                        ...tdStyle, color: C.textSoft, maxWidth: '220px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {row.notes || '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{row.line_count}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: C.brand }}>
                        {row.total_pieces}
                      </td>
                      {canSeeCost && (
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                          {money(row.total_cost)}
                        </td>
                      )}
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {!opening && (
                          <IconBtn
                            danger
                            title="Reverse this transfer"
                            onClick={(e) => { e.stopPropagation(); remove(row); }}
                          >
                            <Trash2 size={15} />
                          </IconBtn>
                        )}
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan={canSeeCost ? 7 : 6} style={{
                          padding: '18px 22px', background: C.rowAlt,
                          borderTop: `1px solid ${C.borderSoft}`,
                        }}>
                          {d?.items?.length ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  <th style={{ ...thStyle, background: 'transparent', padding: '6px 0' }}>Tape</th>
                                  <th style={{ ...thStyle, background: 'transparent', padding: '6px 0' }}>Size</th>
                                  <th style={{ ...thStyle, background: 'transparent', padding: '6px 0', textAlign: 'right' }}>Pieces</th>
                                  {canSeeCost && (
                                    <>
                                      <th style={{ ...thStyle, background: 'transparent', padding: '6px 0', textAlign: 'right' }}>Cost each</th>
                                      <th style={{ ...thStyle, background: 'transparent', padding: '6px 0', textAlign: 'right' }}>Value</th>
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {d.items.map(item => (
                                  <tr key={item.id}>
                                    <td style={{ ...tdStyle, padding: '10px 0', fontWeight: '600' }}>
                                      {item.tape_type_name}
                                      <span style={{ color: C.textMuted, fontWeight: '400' }}>
                                        {' '}{num(item.micron_value)}µ
                                      </span>
                                    </td>
                                    <td style={{ ...tdStyle, padding: '10px 0' }}>
                                      {mm(item.width_mm)} × {yd(item.length_yards)}
                                    </td>
                                    <td style={{ ...tdStyle, padding: '10px 0', textAlign: 'right' }}>
                                      {item.quantity}
                                    </td>
                                    {canSeeCost && (
                                      <>
                                        <td style={{ ...tdStyle, padding: '10px 0', textAlign: 'right', color: C.textSoft }}>
                                          {money2(item.cost_per_piece)}
                                        </td>
                                        <td style={{ ...tdStyle, padding: '10px 0', textAlign: 'right', fontWeight: '600' }}>
                                          {money(item.cost)}
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div style={{ fontSize: '13px', color: C.textMuted }}>Loading…</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {!loading && transfers.length === 0 && (
                <tr>
                  <td colSpan={canSeeCost ? 7 : 6} style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    Nothing transferred yet. Send stock to the shop to start.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {transferForm && (
        <TransferForm
          stock={stock}
          form={transferForm}
          setForm={setTransferForm}
          onSave={saveTransfer}
          onCancel={() => setTransferForm(null)}
          saving={saving}
          canSeeCost={canSeeCost}
        />
      )}

      {openingForm && (
        <OpeningStockForm
          types={types}
          microns={microns}
          form={openingForm}
          setForm={setOpeningForm}
          onSave={saveOpening}
          onCancel={() => setOpeningForm(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

export default TransfersTab;